import { cloneFormData } from '@/modules/templates/lib/dynamic-flow/utils';
import { enrichContext } from '@/modules/templates/services/ai.service';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScreenWithFields, useDynamicForm } from '../DynamicFormContext';

export type AiEnrichmentIndicatorState =
  | { status: 'idle' }
  | { status: 'running'; screenTitle?: string }
  | { status: 'error'; message: string; screenTitle?: string };

export interface UseAiEnrichmentReturn {
  aiEnrichmentState: AiEnrichmentIndicatorState;
  runAiEnrichmentInBackground: (screen: ScreenWithFields | undefined) => void;
}

export function useAiEnrichment(): UseAiEnrichmentReturn {
  const { formData, setEnrichmentContext } = useDynamicForm();

  const [aiEnrichmentState, setAiEnrichmentState] = useState<AiEnrichmentIndicatorState>({
    status: 'idle',
  });

  const aiEnrichmentPendingCount = useRef(0);
  const aiEnrichmentResetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (aiEnrichmentResetTimeout.current) {
        clearTimeout(aiEnrichmentResetTimeout.current);
      }
      abortControllerRef.current?.abort();
    };
  }, []);

  const runAiEnrichmentInBackground = useCallback(
    (screen: ScreenWithFields | undefined) => {
      if (!screen?.aiPrompt) return;

      // Abort any in-flight request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      aiEnrichmentPendingCount.current += 1;
      setAiEnrichmentState({
        status: 'running',
        screenTitle: screen.title,
      });

      void (async () => {
        try {
          const data = await enrichContext(
            screen.aiPrompt || '',
            cloneFormData(formData),
            screen.aiOutputSchema,
            controller.signal
          );

          setEnrichmentContext(data);
          aiEnrichmentPendingCount.current = Math.max(0, aiEnrichmentPendingCount.current - 1);
          if (aiEnrichmentPendingCount.current === 0) {
            if (aiEnrichmentResetTimeout.current) {
              clearTimeout(aiEnrichmentResetTimeout.current);
              aiEnrichmentResetTimeout.current = null;
            }
            setAiEnrichmentState({ status: 'idle' });
          }
        } catch (error) {
          // Silently ignore abort errors
          if (error instanceof DOMException && error.name === 'AbortError') return;

          // Surface the real cause to the console — the UI shows a generic
          // translation key, but the actual error message is what we need
          // when debugging "AI suggestions unavailable" reports.
          console.error('[useAiEnrichment] enrichment failed:', error);

          aiEnrichmentPendingCount.current = Math.max(0, aiEnrichmentPendingCount.current - 1);
          const friendlyMessage =
            (error instanceof Error && error.message) ||
            'AI enrichment is temporarily unavailable.';
          if (aiEnrichmentResetTimeout.current) {
            clearTimeout(aiEnrichmentResetTimeout.current);
          }
          setAiEnrichmentState({
            status: 'error',
            screenTitle: screen.title,
            message: friendlyMessage,
          });
          aiEnrichmentResetTimeout.current = setTimeout(() => {
            if (aiEnrichmentPendingCount.current === 0) {
              setAiEnrichmentState({ status: 'idle' });
              aiEnrichmentResetTimeout.current = null;
            }
          }, 5000);
        }
      })();
    },
    [formData, setEnrichmentContext]
  );

  return {
    aiEnrichmentState,
    runAiEnrichmentInBackground,
  };
}
